import { PasswordInputComp } from '@/components/passwordInput/passwordInputComp';
import { PopupModel } from '@/components/popupModal/popupModal';
import { useTranslation } from 'react-i18next';
import { Input, UseDisclosureProps } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { validatePasswordFormat, validateVChatIdFormat } from '@/service/utils';

export type IBindingVChatModelProps = {
    title?: string;
    onSave: (password: string, vChatId: string) => void;
} & UseDisclosureProps;

export const WarningMessageEnum = {
    WrongFormat: 'WrongFormat',
    NewConfirmNotTheSame: 'NewConfirmNotTheSame',
    NewOldTheSame: 'NewOldTheSame',
    WrongPassword: 'WrongPassword',
    NewNeedValue: 'NewNeedValue',
} as const;

export type IWarringMessageType = keyof typeof WarningMessageEnum;

export const BindingVChatPopup = (props: IBindingVChatModelProps) => {
    const { t } = useTranslation();
    const [password, setPassword] = useState('');
    const [vChatId, setVChatId] = useState('');
    const [passwordMsg, setPasswordMsg] = useState('');
    const [vChatIdMsg, setVChatIdMsg] = useState('');

    const warringMessage: { [key: string]: string } = {
        [WarningMessageEnum.WrongFormat]: t('Msg_Error_ResetPassword_WrongFormat'),
    };

    const warringVChatMessage: { [key: string]: string } = {
        [WarningMessageEnum.WrongFormat]: t('Msg_Error_ResetPassword_WrongFormat'),
    };

    const handlePassword = (password: string) => {
        setPassword(password);
        if (validatePasswordFormat(password, [6, 20])) {
            setPasswordMsg('');
        } else {
            setPasswordMsg(warringMessage[WarningMessageEnum.WrongFormat]);
        }
    };

    const handleVChatId = (vChatId: string) => {
        setVChatId(vChatId.toLowerCase());
        if (validateVChatIdFormat(vChatId)) {
            setVChatIdMsg('');
        } else {
            setVChatIdMsg(warringVChatMessage[WarningMessageEnum.WrongFormat]);
        }
    };

    const canSave = useMemo<boolean>(() => {
        if (passwordMsg.length > 0 || vChatIdMsg.length > 0) {
            return false;
        }

        if (password.length === 0 || vChatId.length === 0) {
            return false;
        }

        return true;
    }, [passwordMsg, vChatIdMsg, password, vChatId]);

    const handleOnSave = () => {
        const errMsgs = [passwordMsg, vChatIdMsg];
        if (errMsgs.every((item) => item === '')) {
            props.onSave(password, vChatId.trim());
        }
    };

    const hintContents = ['绑定成功，将会自动登出，请重新登入。', t('Msg_VChat_Password')];

    return (
        <>
            <PopupModel {...props} size="lg">
                <div className="flex-col flex h-[280px]">
                    <div className="flex-col basis-6/12 mt-2 grow flex">
                        <div className="flex grow">
                            <div className="basis-1/6 text-right text-sm mt-1 text-gray-500">
                                <span className=" text-red-600">*</span>
                                <span>{t('Th_VChatId')}:</span>
                            </div>
                            <div className="basis-5/6 ml-1 flex-col flex">
                                <Input
                                    css="w-[150px] border-gray-300 border-2 rounded"
                                    maxLength={10}
                                    placeholder={t('Msg_PleaseEnterVChatId')}
                                    type="text"
                                    onChange={(e) => {
                                        handleVChatId(e.target.value);
                                    }}
                                />
                                <div className="h-1 text-red-500 text-xs">{vChatIdMsg}</div>
                            </div>
                        </div>
                        <div className="flex grow ">
                            <div className="basis-1/6 text-right text-sm mt-1 text-gray-500">
                                <span className=" text-red-600">*</span>
                                <span>{t('Txt_password')}:</span>
                            </div>
                            <div className="basis-5/6 ml-1">
                                <PasswordInputComp placeHolder={t('Msg_VChat_Password')} onChange={handlePassword} />
                                <div className="h-1 text-red-500 text-xs">{passwordMsg}</div>
                            </div>
                        </div>
                    </div>
                    {/* Message */}
                    <div data-note className="basis-3/12 flex grow mt-3">
                        <div data-note className="basis-1/6  mb-4 text-xs" />
                        <div data-note className="basis-5/6  mb-4 text-xs">
                            <ul className="ml-4 list-disc">
                                {hintContents.map((text, i) => {
                                    return (
                                        <li key={`note-${i}`} className="text-red-400 whitespace-pre-line">
                                            {text}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                    <div className="basis-2/12 mt-2 text-center grow">
                        <button className="p-2 m-2 border border-affiliate rounded hover:bg-gray-200" onClick={props.onClose}>
                            <label className="px-1 cursor-pointer">{t('Btn_Cancle_VChatId')}</label>
                        </button>
                        <button className="p-2 bg-affiliate text-white rounded hover:bg-gray-500" disabled={!canSave} onClick={handleOnSave}>
                            <label className="px-1 cursor-pointer">{t('Btn_Save_VChatId')}</label>
                        </button>
                    </div>
                </div>
            </PopupModel>
        </>
    );
};
